import { useState, useMemo } from "react"
import { Search, X, Check, Forward } from "lucide-react"
import type { Conversation, Message } from "./types"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { AvatarPhoto } from "./avatar-photo"
import { useAuthStore } from "@/features/auth/store/auth.store"

interface ForwardMessageDialogProps {
  open: boolean
  /** Everything being forwarded, in the order it will be delivered. */
  messages: Message[]
  contacts: Conversation[]
  onClose: () => void
  onForward: (contactIds: string[]) => void
}

/**
 * What the user is about to forward. An attachment sent without a caption has no
 * text, which rendered as a pair of empty quotes — name the file (or its kind)
 * instead.
 */
function previewLabel(message: Message): string {
  const text = message.text?.trim()
  if (text) return `"${text}"`
  if (message.attachmentName) return message.attachmentName
  switch (message.type) {
    case 'image': return 'Photo'
    case 'video': return 'Video'
    case 'audio': return 'Audio'
    case 'file':  return 'Document'
    default:      return 'Attachment'
  }
}

function Avatar({ name, imageUrl, contactId, size = 40 }: { name: string; imageUrl?: string; contactId?: string; size?: number }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-primary/20 text-primary", "bg-violet-500/20 text-violet-600", "bg-emerald-500/20 text-emerald-600", "bg-amber-500/20 text-amber-600"]
  const colorIdx = name.charCodeAt(0) % colors.length
  return (
    <div
      className={`rounded-full flex items-center justify-center text-xs font-semibold shrink-0 overflow-hidden ${colors[colorIdx]}`}
      style={{ width: size, height: size }}
    >
      <AvatarPhoto src={imageUrl} alt={name} className="w-full h-full object-cover" fallback={initials} contactId={contactId} />
    </div>
  )
}

export function ForwardMessageDialog({ open, messages, contacts, onClose, onForward }: ForwardMessageDialogProps) {
  const [search, setSearch] = useState("")
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const myUserId = useAuthStore((s) => s.presenceId)

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return contacts.filter(c => !c.isArchived && c.name.toLowerCase().includes(q))
  }, [contacts, search])

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handleForward = () => {
    if (selected.size === 0) return
    onForward([...selected])
    setSelected(new Set())
    setSearch("")
    onClose()
  }

  const handleClose = () => {
    setSelected(new Set())
    setSearch("")
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && handleClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Forward className="w-4 h-4 text-primary" />
            {messages.length > 1 ? `Forward ${messages.length} messages` : "Forward message"}
          </DialogTitle>
        </DialogHeader>

        {/* Preview of what's being forwarded. A multi-select names the count —
            previewing only the first message was how it used to hide that just
            one of them was actually being sent. */}
        {messages.length > 0 && (
          <div className="px-4 py-2 bg-muted/40 border-b border-border">
            <p className="text-xs text-muted-foreground mb-0.5">Forwarding:</p>
            <p className="text-sm text-foreground line-clamp-2">
              {messages.length === 1
                ? previewLabel(messages[0])
                : `${messages.length} messages`}
            </p>
          </div>
        )}

        {/* Search */}
        <div className="px-4 py-2 border-b border-border">
          <div className="flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search contacts…"
              className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground"
            />
            {search && (
              <button onClick={() => setSearch("")}>
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>

        {/* Selected chips */}
        {selected.size > 0 && (
          <div className="flex gap-2 px-4 py-2 flex-wrap border-b border-border">
            {[...selected].map(id => {
              const c = contacts.find(x => x.id === id)
              if (!c) return null
              return (
                <button
                  key={id}
                  onClick={() => toggle(id)}
                  title={c.name}
                  className="flex items-center gap-1.5 max-w-[10rem] bg-primary/10 text-primary text-xs font-medium px-2.5 py-1 rounded-full hover:bg-primary/20 transition-colors"
                >
                  <span className="truncate">{c.name}</span>
                  <X className="w-3 h-3 shrink-0" />
                </button>
              )
            })}
          </div>
        )}

        {/* Contact list */}
        <div className="max-h-64 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">No contacts found</div>
          ) : (
            filtered.map(c => {
              const isSelected = selected.has(c.id)
              return (
                <button
                  key={c.id}
                  onClick={() => toggle(c.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <Avatar
                    name={c.name}
                    imageUrl={c.imageUrl}
                    contactId={!c.isGroup ? c.participants?.find(p => p.id !== myUserId)?.id : undefined}
                  />
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{c.isGroup ? "Group" : c.lastMessage}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all shrink-0 ${isSelected ? "bg-primary border-primary" : "border-border"}`}>
                    {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>
                </button>
              )
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-border flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleClose}>Cancel</Button>
            <Button size="sm" onClick={handleForward} disabled={selected.size === 0}>
              <Forward className="w-4 h-4 mr-1.5" />
              Forward
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
